import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  ArrowLeft,
  Camera,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Image,
  Calendar
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function PhotoCompare() {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [photoType, setPhotoType] = useState("front"); // front, side, back

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/photos`);
      const allPhotos = response.data.photos || [];
      setPhotos(allPhotos);
      
      // Auto-select first and last photos of same type
      const frontPhotos = allPhotos.filter(p => p.photo_type === "front");
      if (frontPhotos.length >= 2) {
        setBeforePhoto(frontPhotos[frontPhotos.length - 1]); // oldest
        setAfterPhoto(frontPhotos[0]); // newest
      }
    } catch (error) {
      console.error("Failed to fetch photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPhotosByType = (type) => {
    return photos.filter(p => p.photo_type === type);
  };

  const handleTypeChange = (type) => {
    setPhotoType(type);
    const typePhotos = getPhotosByType(type);
    if (typePhotos.length >= 2) {
      setBeforePhoto(typePhotos[typePhotos.length - 1]);
      setAfterPhoto(typePhotos[0]);
    } else if (typePhotos.length === 1) {
      setBeforePhoto(typePhotos[0]);
      setAfterPhoto(null);
    } else {
      setBeforePhoto(null);
      setAfterPhoto(null);
    }
  };

  const loadFullPhoto = async (photoId) => {
    try {
      const response = await axios.get(`${API}/photos/${photoId}`);
      return response.data.image_data;
    } catch (error) {
      return null;
    }
  };

  const [beforeImageData, setBeforeImageData] = useState(null);
  const [afterImageData, setAfterImageData] = useState(null);

  useEffect(() => {
    const loadImages = async () => {
      if (beforePhoto?.id) {
        const data = await loadFullPhoto(beforePhoto.id);
        setBeforeImageData(data);
      }
      if (afterPhoto?.id) {
        const data = await loadFullPhoto(afterPhoto.id);
        setAfterImageData(data);
      }
    };
    loadImages();
  }, [beforePhoto, afterPhoto]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/progress" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">BEFORE / AFTER</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Photo Type Selector */}
        <div className="flex justify-center gap-2 mb-8">
          {["front", "side", "back"].map((type) => (
            <Button
              key={type}
              variant={photoType === type ? "default" : "outline"}
              onClick={() => handleTypeChange(type)}
              className={`uppercase font-bold tracking-widest rounded-sm ${
                photoType === type ? 'bg-primary' : ''
              }`}
            >
              {type}
            </Button>
          ))}
        </div>

        {beforeImageData && afterImageData ? (
          <>
            {/* Comparison Slider */}
            <Card className="bg-card border-border mb-6 overflow-hidden">
              <CardContent className="p-0">
                <div className="relative aspect-[3/4] overflow-hidden">
                  {/* After Image (full width) */}
                  <div className="absolute inset-0">
                    <img 
                      src={afterImageData} 
                      alt="After"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-accent/80 text-black text-xs font-bold uppercase rounded-sm">
                      After - Day {afterPhoto?.day}
                    </div>
                  </div>
                  
                  {/* Before Image (clipped) */}
                  <div 
                    className="absolute inset-0 overflow-hidden"
                    style={{ width: `${sliderPosition}%` }}
                  >
                    <img 
                      src={beforeImageData} 
                      alt="Before"
                      className="w-full h-full object-cover"
                      style={{ width: `${100 / (sliderPosition / 100)}%` }}
                    />
                    <div className="absolute top-4 left-4 px-3 py-1 bg-primary/80 text-white text-xs font-bold uppercase rounded-sm">
                      Before - Day {beforePhoto?.day}
                    </div>
                  </div>
                  
                  {/* Slider Line */}
                  <div 
                    className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
                    style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                  >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <ChevronLeft className="h-4 w-4 text-gray-800 -mr-1" />
                      <ChevronRight className="h-4 w-4 text-gray-800 -ml-1" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Slider Control */}
            <div className="mb-8">
              <Slider
                value={[sliderPosition]}
                onValueChange={([val]) => setSliderPosition(val)}
                min={0}
                max={100}
                step={1}
                className="w-full"
              />
            </div>

            {/* Photo Details */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-primary/10 border-primary/30">
                <CardContent className="p-4 text-center">
                  <div className="text-xs text-primary uppercase tracking-wider mb-1">Before</div>
                  <div className="text-2xl font-black">Day {beforePhoto?.day}</div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(beforePhoto?.timestamp).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-accent/10 border-accent/30">
                <CardContent className="p-4 text-center">
                  <div className="text-xs text-accent uppercase tracking-wider mb-1">After</div>
                  <div className="text-2xl font-black">Day {afterPhoto?.day}</div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(afterPhoto?.timestamp).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Camera className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold uppercase tracking-tighter mb-2">Need More Photos</h3>
              <p className="text-muted-foreground mb-6">
                Upload at least 2 {photoType} view photos to compare your transformation.
              </p>
              <Link to="/progress">
                <Button className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm">
                  Upload Photos
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* All Photos Grid */}
        {photos.length > 0 && (
          <Card className="bg-card border-border mt-8">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <Image className="h-5 w-5 text-primary" />
                Select Photos to Compare
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {getPhotosByType(photoType).map((photo, i) => (
                  <motion.div
                    key={photo.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.05 }}
                    className={`relative aspect-square rounded-sm overflow-hidden cursor-pointer border-2 transition-all ${
                      photo.id === beforePhoto?.id 
                        ? 'border-primary ring-2 ring-primary/50' 
                        : photo.id === afterPhoto?.id 
                        ? 'border-accent ring-2 ring-accent/50'
                        : 'border-transparent hover:border-white/30'
                    }`}
                    onClick={async () => {
                      if (!beforePhoto || photo.id === beforePhoto?.id) {
                        setBeforePhoto(photo);
                        const data = await loadFullPhoto(photo.id);
                        setBeforeImageData(data);
                      } else if (!afterPhoto || photo.id === afterPhoto?.id) {
                        setAfterPhoto(photo);
                        const data = await loadFullPhoto(photo.id);
                        setAfterImageData(data);
                      } else {
                        // Replace before photo
                        setBeforePhoto(photo);
                        const data = await loadFullPhoto(photo.id);
                        setBeforeImageData(data);
                      }
                    }}
                  >
                    <div className="absolute inset-0 bg-secondary flex items-center justify-center">
                      <Image className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                      <div className="text-xs font-bold text-white">Day {photo.day}</div>
                    </div>
                    {photo.id === beforePhoto?.id && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary text-white text-xs font-bold rounded-sm">
                        Before
                      </div>
                    )}
                    {photo.id === afterPhoto?.id && (
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-accent text-black text-xs font-bold rounded-sm">
                        After
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
